import { EventEmitter } from 'events';

/**
 * WebSocket service for real-time price updates
 */
class PriceWebSocketService extends EventEmitter {
  private socket: WebSocket | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isConnecting: boolean = false;

  // Update WebSocket connection URL to adapt to deployment environment
  private getWebSocketUrl(): string {
    // Use the same protocol and host as the current page
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/ws/price`;
  }

  /**
   * Connect to the WebSocket
   */
  public connect(): void {
    if (this.isConnected() || this.isConnecting) return;

    this.isConnecting = true;

    try {
      const url = this.getWebSocketUrl();
      this.socket = new WebSocket(url);

      this.socket.onopen = () => {
        this.isConnecting = false;
        this.emit('connected');

        // If we were trying to reconnect, clear the timer
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Extract the price from the message
          if (data.price) {
            const price = parseFloat(data.price);
            this.emit('price', price);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
          // Don't emit error here - just log it
        }
      };

      this.socket.onerror = (event) => {
        // Log the error but don't emit it (prevents unhandled errors)
        console.warn('WebSocket error - will try to reconnect');
      };

      this.socket.onclose = () => {
        this.socket = null;
        this.isConnecting = false;
        this.emit('disconnected');

        // Try to reconnect after 5 seconds
        this.scheduleReconnect();
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.isConnecting = false;

      // Try to reconnect after 5 seconds
      this.scheduleReconnect();
    }
  }

  /**
   * Schedule a reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, 5000); // Reconnect every 5 seconds
  }

  /**
   * Disconnect from the WebSocket
   */
  public disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Check if the WebSocket is connected
   */
  public isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }
}

// Create singleton instance
const priceWebSocket = new PriceWebSocketService();
export default priceWebSocket;
